import React, { useEffect, CSSProperties, useState, useRef } from "react";
import styles from "./ButtonWithArrow.module.css";

type ButtonWithArrowProps = {
  isLink?: boolean;
  isArrowAnimation?: boolean;
  text: string;
  fontFamily?: string;
  fontSize?: string | number;
  textColor?: string;
  buttonColor?: any;
  hoverTextColor?: string;
  hoverButtonColor?: string;
  isTransparentWithBorder?: boolean;
  borderColor?: string;
  style?: CSSProperties;
  url?: string;
  background?: string;
  direction?: "left" | "right";
  isSemitransparentOnHover?: boolean;
  isArrowInFront?: boolean;
  isGradient?: boolean;
  isAnimated?: boolean;
  isPrimaryAction?: boolean;
  fullWidthOnMobile?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
};

const ButtonWithArrow: React.FC<ButtonWithArrowProps> = ({
  text,
  isLink = false,
  isArrowAnimation = true,
  fontFamily = "Inter, Arial, sans-serif",
  fontSize,// = "1.1rem",
  textColor = "#008080",
  buttonColor = "transparent",
  hoverTextColor = "#31d5c8",
  hoverButtonColor = "#9bf00b",
  isTransparentWithBorder = false,
  borderColor = "#31d5c8",
  isGradient = false,
  isAnimated = true,
  isPrimaryAction = false,
  fullWidthOnMobile,
  background,
  style = {},
  url,
  direction = "right",
  isSemitransparentOnHover = false,
  isArrowInFront = false,
  onClick,
}) => {
  const [isSmallScreen] = useState(window.innerWidth < 1025);
  const [hovered, setHovered] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [btnWidth, setBtnWidth] = useState<number | undefined>(undefined);

  const handleClick = () => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // On mouse enter, measure width once and set
  const handleMouseEnter = () => {
    setHovered(true);
    if (btnRef.current && !isLink && isArrowAnimation) {
      // Add arrow width if needed
      const arrowWidth = 28;
      setBtnWidth(btnRef.current.clientWidth + arrowWidth);
    }
  };

  // On mouse leave, reset width
  const handleMouseLeave = () => {
    setHovered(false);
    setBtnWidth(undefined);
  };

  // Dynamic style variables for inline logic
  // Compose className based on props and state
  const classNames = [
    styles.buttonWithArrow,
    isLink ? styles.isLink : "",
    isTransparentWithBorder ? styles.transparentWithBorder : "",
    hovered ? styles.hovered : "",
    isArrowInFront ? styles.arrowInFront : "",
    isGradient ? styles.gradient : "",
    isPrimaryAction ? styles.primaryAction : "",
    isAnimated ? styles.animated : "",
    !isLink && hovered ? styles.expanded : "",
    fullWidthOnMobile ? styles.fullWidthMobile : ""
  ].filter(Boolean).join(" ");

  // CSS variable style for dynamic values
  // Build dynamic style object (not string)
  const dynamicStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: !isLink ? "12px" : "0px",
    padding: !isLink ? "16px 18px" : undefined,
    borderRadius: !isLink ? "24px" : undefined,
    border: (isTransparentWithBorder && !hovered) || hoverButtonColor === "transparent" ? `3px solid ${borderColor}` : undefined,
    // background: hovered ? `linear-gradient(180deg, ${buttonColor}  0%, ${hoverButtonColor} 100%)`
    //  : `linear-gradient(180deg, ${hoverButtonColor} 0%, ${buttonColor} 100%)`,
    ...(isPrimaryAction
      ? {
          backgroundColor: undefined,
          background: undefined,
        }
      : {
          backgroundColor: hovered ? hoverButtonColor : buttonColor,
        }),
    color: hovered ? hoverTextColor : textColor,
    transition: "background 0.2s ease, color 0.2s, width 0.2s, transform 3.5s cubic-bezier(.77, 0, .18, 1)",
    // animation: "background 10s ease-out 2.3s forwards",
    fontFamily,
    fontSize,
    fontWeight: 600,
    cursor: "pointer",
    outline: "none",
    boxSizing: "border-box",
    width: !isLink && hovered && btnWidth ? `${btnWidth}px` : undefined,
    ...style,
  };

  return (
    <button
      ref={btnRef}
      className={classNames}
      style={dynamicStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick ?? handleClick}
      type="button"
    >
      {
      !isLink && !isArrowAnimation ? (
              // <span className={`${hovered ? styles.backgroundAnimate : ''} ${styles.buttonText}`}>{text}</span>
               <span className={styles.buttonText}>{text}</span>
      ) : 
      isLink && !isArrowAnimation ? (
        <span
          className={styles.linkTextWrapper}
          style={{
            display: "inline-block",
            position: "relative",
            verticalAlign: "middle",
            lineHeight: "1.2",
            fontWeight: 600,
          }}
        >
          <span>{text}</span>
          <span
            className={[
              styles.linkUnderline,
              hovered ? styles.linkUnderlineHovered : "",
            ].filter(Boolean).join(" ")}
            style={{
              display: "block",
              position: "absolute",
              left: 0,
              right: 0,
              height: !isSmallScreen ? "5px" : "3px",
              background: borderColor,
              borderRadius: "2px",
              transition: !isSmallScreen
                ? "transform 0.3s cubic-bezier(0.77,0,0.175,1)"
                : "none",
              transform: hovered
                ? "translateY(-7px)"
                : "translateY(0)",
              marginTop: !isSmallScreen ? "7px" : undefined,
            }}
          />
        </span>
      ) :
        isLink || isArrowInFront ? (
          <span
            className={styles.arrowInFrontWrapper}
            style={{
              display: "inline-flex",
              alignItems: "center",
              position: "relative",
              width: "max-content",
            }}
          >
            <span
              className={[
                styles.arrowSvgAbsolute,
                hovered ? styles.arrowSvgAbsoluteHovered : "",
              ].filter(Boolean).join(" ")}
              style={{
                position: "absolute",
                left: 0,
                top: "50%",
                transform: hovered
                  ? "translateY(-100%) translateX(0)"
                  : "translateY(-100%) translateX(-28px)",
                opacity: hovered ? 1 : 0,
                transition:
                  "opacity 0.3s cubic-bezier(0.77,0,0.175,1), transform 0.3s cubic-bezier(0.77,0,0.175,1)",
                pointerEvents: "none",
                width: "28px",
                height: "18px",
                display: "inline-block",
              }}
            >
              {/* SVG Arrow */}
              {direction === "right" && (
                <svg width="28" height="18" viewBox="0 0 28 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 9H26" stroke={hovered ? hoverTextColor : textColor} strokeWidth="3" strokeLinecap="round" />
                  <path d="M19 3L26 9L19 15" stroke={hovered ? hoverTextColor : textColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span
              className={[
                styles.arrowInFrontText,
                hovered ? styles.arrowInFrontTextHovered : "",
              ].filter(Boolean).join(" ")}
              style={{
                display: "inline-block",
                verticalAlign: "middle",
                transition: "transform 0.3s cubic-bezier(0.77,0,0.175,1)",
                transform: hovered ? "translateX(28px)" : "translateX(0)",
                marginLeft: "0px",
                lineHeight: "1.2",
                fontWeight: 600,
                borderBottom:
                  isSmallScreen && isLink
                    ? `3px solid ${textColor}`
                    : hovered && !isArrowInFront
                      ? `3px solid ${textColor}`
                      : "none",
              }}
            >
              {text}
            </span>
          </span>
        ) : (
          <>
            <span className={styles.buttonText}>{text}</span>
            <span
              className={[
                styles.arrowSvgWrapper,
                hovered ? styles.arrowSvgWrapperHovered : "",
              ].filter(Boolean).join(" ")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                transition: "transform 0.3s cubic-bezier(0.77,0,0.175,1), opacity 0.3s cubic-bezier(0.77,0,0.175,1)",
                transform: hovered ? "translateX(10px)" : "none",
              }}
            >
              {/* SVG Arrow */}
              <svg width="28" height="18" viewBox="0 0 28 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 9H26" stroke={hovered ? hoverTextColor : textColor} strokeWidth="3" strokeLinecap="round" />
                <path d="M19 3L26 9L19 15" stroke={hovered ? hoverTextColor : textColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </>
          )}
   </button>
  ); 
            //  </span>
//     </span>
//   ) : (
//     <>
//       <span className={styles.buttonText}>{text}</span>
//       <span
//         className={[
//           styles.arrowSvgWrapper,
//           hovered ? styles.arrowSvgWrapperHovered : "",
//         ].filter(Boolean).join(" ")}
//       >
//         <svg width="28" height="18" viewBox="0 0 28 18" fill="none">
//           <line
//             x1="3"
//             y1="9"
//             x2="20"
//             y2="9"
//             stroke={hovered ? hoverTextColor : textColor}
//             strokeWidth="3"
//             strokeLinecap="round"
//           />
//           <polyline
//             points="14,4 22,9 14,14"
//             fill="none"
//             stroke={hovered ? hoverTextColor : textColor}
//             strokeWidth="3"
//             strokeLinecap="round"
//             strokeLinejoin="round"
//           />
//         </svg>
//       </span>
//     </>
//   )
// }
//     </button >
//   );
};

export default React.memo(ButtonWithArrow);